// src/JourneyOverview.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import JourneyService, { normalizeJourneyProblemKey } from "./services/JourneyService";
import { getBlueprintForProblem } from "./JourneyBlueprints";
import { getProblemByKey, getProblemTitleForLang } from "./journeyProblems";
import { dirForLang, normalizeLang } from "./i18n";
import { buildInsights } from "./services/insights";
import { getSystemStrings } from "./strings/systemStrings";
import { useAuth } from "./auth/AuthContext";
import { pickGenderText } from "./utils/genderText";
import { PHASE1_PROBLEM_KEY } from "./phase1/config";
import { resolveUserFacingText } from "./utils/userFacingText";
import "./JourneyOverview.css";

function useQuery() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const lang = normalizeLang(params.get("lang") || "he");
  const problemKey = String(params.get("problemKey") || "").trim();
  const runId = String(params.get("runId") || "").trim();
  return { lang, problemKey, runId, params };
}

const DNA_PROBLEM_KEYS = new Set([
  "rel_breakup_fresh_pain",
  "rel_breakup_fresh_pain_phase1",
  "rel_breakup_fresh_pain_reference_18d",
  "obsessive_longing",
  "overthinking_after_breakup",
  "jealousy_ex_moves_on",
  "lack_of_closure_after_breakup",
  "fear_leaving_bad_relationship",
  "lack_of_trust_in_relationship",
  "pursuer_distancer_cycle",
  "difficulty_expressing_needs_and_boundaries_with_partner",
  "conditional_relationship_fear_of_abandonment",
  "romantic_loneliness",
  "fear_starting_relationship_dating_anxiety",
  "repeated_attraction_to_destructive_partners",
  "difficulty_opening_up_on_dates",
  "i_am_not_enough",
  "constant_comparison_to_others",
  "chronic_sense_of_failure",
  "difficulty_receiving_compliments_achievements",
  "dependence_on_external_approval",
  "harsh_self_criticism",
  "perfectionism_fear_of_mistakes_procrastination",
  "catastrophic_thoughts",
  "anxiety_general",
  "anxiety_vigilance",
]);

const RUN_COPY = {
  he: {
    title: "המסעות שלי",
    activeSection: "המסע הפעיל שלי",
    historySection: "מסעות קודמים",
    startNew: "להתחיל מסע חדש",
    active: "המסע הפעיל",
    switchTo: "לעבור למסע הזה",
    continueRun: "להמשיך מכאן",
    completed: "הושלם",
    inProgress: "בתהליך",
    runLabel: (index) => `מסע ${index}`,
    dayProgress: (day, total) => `יום ${day}/${total}`,
    startedOn: "התחלתי",
    waitingDay: (day) => `יום ${day} מחכה לך`,
    completedJourney: "המסע הושלם",
    historyHint: "כאן מופיעים מסעות קודמים של אותו נושא.",
    noRuns: "עדיין אין מסעות קודמים לנושא הזה.",
  },
  en: {
    title: "My journeys",
    activeSection: "My active journey",
    historySection: "Previous journeys",
    startNew: "Start a new journey",
    active: "Active journey",
    switchTo: "Open this journey",
    continueRun: "Continue here",
    completed: "Completed",
    inProgress: "In progress",
    runLabel: (index) => `Journey ${index}`,
    dayProgress: (day, total) => `Day ${day}/${total}`,
    startedOn: "Started",
    waitingDay: (day) => `Day ${day} is waiting for you`,
    completedJourney: "Journey completed",
    historyHint: "Earlier journeys on this same topic appear here.",
    noRuns: "No previous journeys for this topic yet.",
  },
  es: {
    title: "Mis viajes",
    activeSection: "Mi viaje activo",
    historySection: "Viajes anteriores",
    startNew: "Empezar un viaje nuevo",
    active: "Viaje activo",
    switchTo: "Abrir este viaje",
    continueRun: "Seguir desde aqui",
    completed: "Completado",
    inProgress: "En curso",
    runLabel: (index) => `Viaje ${index}`,
    dayProgress: (day, total) => `Dia ${day}/${total}`,
    startedOn: "Empece",
    waitingDay: (day) => `El dia ${day} te esta esperando`,
    completedJourney: "Recorrido completado",
    historyHint: "Aqui aparecen viajes anteriores sobre el mismo tema.",
    noRuns: "Todavia no hay viajes anteriores para este tema.",
  },
};

function getRunCopy(lang) {
  return RUN_COPY[lang] || RUN_COPY.he;
}

function formatRunDate(value, lang) {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat(lang === "he" ? "he-IL" : lang === "es" ? "es-ES" : "en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

const OVERVIEW_SCREEN_COPY = {
  he: {
    currentBadge: "היום שלך",
    currentLine: "איפה אני עכשיו במסע",
    reopenDay: "לפתוח שוב",
    futureDay: "עדיין לא נפתח",
    timelineTitle: "הדרך שלי במסע",
    timelineSubtitle: "מה כבר עברתי, איפה אני עכשיו, ומה מחכה לי בהמשך.",
    heroCta: "להמשיך מכאן",
    heroKicker: "איפה אני עכשיו?",
    completedHint: "עברתי את זה",
  },
  en: {
    currentBadge: "Today",
    currentLine: "This is where I am now",
    reopenDay: "Open again",
    futureDay: "Not open yet",
    timelineTitle: "My path in this journey",
    timelineSubtitle: "What I already moved through, where I am now, and what waits ahead.",
    heroCta: "Continue from here",
    heroKicker: "Where I am now",
    completedHint: "I moved through this",
  },
  es: {
    currentBadge: "Hoy",
    currentLine: "Aqui estoy ahora",
    reopenDay: "Abrir de nuevo",
    futureDay: "Aun no se abre",
    timelineTitle: "Mi camino en este viaje",
    timelineSubtitle: "Lo que ya recorri, donde estoy ahora y lo que me espera despues.",
    heroCta: "Continuar desde aqui",
    heroKicker: "Donde estoy ahora",
    completedHint: "Ya pase por esto",
  },
};

function getOverviewScreenCopy(lang) {
  return OVERVIEW_SCREEN_COPY[lang] || OVERVIEW_SCREEN_COPY.he;
}

const MOOD_EXPERIENCE_COPY = {
  he: {
    title: "איפה אני עכשיו?",
    subtitle: "רק כדי לכוון את המסע שלך — אין תשובה נכונה",
    states: {
      1: {
        icon: "🌱",
        lines: ["אולי היום לא צריך להבין הכול", "רק להיות רגע עם מה שיש"],
      },
      2: {
        icon: "🌿",
        lines: ["משהו בך קצת כבד היום", "ואפשר ללכת לאט"],
      },
      3: {
        icon: "🌿",
        lines: [
          {
            male: "אתה בדיוק באמצע",
            female: "את בדיוק באמצע",
            other: "את/ה בדיוק באמצע",
          },
          "וזה מקום טוב להתחיל ממנו",
        ],
      },
      4: {
        icon: "🍃",
        lines: ["יש בך כבר תנועה", "אפשר להתקרב עוד קצת פנימה"],
      },
      5: {
        icon: "🌳",
        lines: ["משהו פתוח בך היום", "אולי זה רגע להעמיק"],
      },
    },
  },
  en: {
    title: "Where am I right now?",
    subtitle: "Just to guide your journey. There is no right answer.",
    states: {
      1: {
        icon: "🌱",
        lines: ["Maybe today doesn't need to explain everything", "Only stay for a moment with what is here"],
      },
      2: {
        icon: "🌿",
        lines: ["Something feels a little heavy in you today", "And it is okay to move slowly"],
      },
      3: {
        icon: "🌿",
        lines: ["You are right in the middle of it", "And that is a good place to begin"],
      },
      4: {
        icon: "🍃",
        lines: ["There is already some movement in you", "You can come a little closer inside"],
      },
      5: {
        icon: "🌳",
        lines: ["Something feels open in you today", "Maybe this is a moment to go deeper"],
      },
    },
  },
  es: {
    title: "¿Dónde estoy ahora?",
    subtitle: "Solo para orientar tu viaje. No hay una respuesta correcta.",
    states: {
      1: {
        icon: "🌱",
        lines: ["Quizá hoy no hace falta entenderlo todo", "Solo estar un momento con lo que hay"],
      },
      2: {
        icon: "🌿",
        lines: ["Hay algo un poco pesado en ti hoy", "Y puedes ir despacio"],
      },
      3: {
        icon: "🌿",
        lines: ["Estás justo en el medio", "Y es un buen lugar para empezar"],
      },
      4: {
        icon: "🍃",
        lines: ["Ya hay movimiento en ti", "Puedes acercarte un poco más hacia dentro"],
      },
      5: {
        icon: "🌳",
        lines: ["Hay algo abierto en ti hoy", "Quizá este sea un momento para profundizar"],
      },
    },
  },
};

function getMoodExperienceCopy(lang, gender, journeyTopic) {
  const copy = MOOD_EXPERIENCE_COPY[lang] || MOOD_EXPERIENCE_COPY.he;
  const topic = String(journeyTopic || "").trim();
  const baseSubtitle = resolveUserFacingText(copy.subtitle, { lang, gender, fallback: "" });
  const subtitle =
    topic && baseSubtitle
      ? lang === "he"
        ? `«${topic}» — ${baseSubtitle}`
        : lang === "es"
          ? `«${topic}» — ${baseSubtitle}`
          : `“${topic}” — ${baseSubtitle}`
      : baseSubtitle;
  return {
    title: resolveUserFacingText(copy.title, { lang, gender, fallback: "" }),
    subtitle,
    states: Object.fromEntries(
      Object.entries(copy.states).map(([value, state]) => [
        Number(value),
        {
          icon: state.icon,
          lines: state.lines.map((line) =>
            resolveUserFacingText(line, { lang, gender, fallback: "" })
          ),
        },
      ])
    ),
  };
}

function isGenderNode(node) {
  return (
    !!node &&
    typeof node === "object" &&
    !Array.isArray(node) &&
    ("male" in node || "female" in node || "other" in node)
  );
}

function resolveNodeLines(node, lang, gender) {
  let localized = node;

  if (localized && typeof localized === "object" && !Array.isArray(localized) && !isGenderNode(localized)) {
    if (localized[lang] != null) localized = localized[lang];
    else if (localized.he != null) localized = localized.he;
    else if (localized.en != null) localized = localized.en;
    else if (localized.es != null) localized = localized.es;
  }

  if (isGenderNode(localized)) {
    localized = pickGenderText(localized, gender);
  }

  const values = Array.isArray(localized) ? localized : [localized];
  return values
    .map((value) => resolveUserFacingText(value, { lang, gender, fallback: "" }))
    .flatMap((value) => String(value || "").split(/\n+/))
    .map((line) => line.trim())
    .filter(Boolean);
}

function pickMeaningfulPreview(lines = []) {
  return (
    lines.find(
      (line) =>
        line &&
        !["רגע.", "Pause.", "Pausa."].includes(line) &&
        line.length >= 8
    ) ||
    lines.find(Boolean) ||
    ""
  );
}

export default function JourneyOverview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { lang, problemKey, runId, params } = useQuery();
  const normalizedProblemKey = normalizeJourneyProblemKey(problemKey);
  const dir = dirForLang(lang);
  const t = getSystemStrings(lang, user?.gender).journeyOverview;

  const [, setMeta] = useState(null);
  const [intake, setIntake] = useState(null);
  const [dayStates, setDayStates] = useState({});
  const [journeyRuns, setJourneyRuns] = useState([]);
  const [insights, setInsights] = useState(null);
  const [serverInsights, setServerInsights] = useState(null);
  const [mood, setMood] = useState(3);
  const [energy, setEnergy] = useState(3);
  const [signalsTouched, setSignalsTouched] = useState(false);
  const [runBusy, setRunBusy] = useState(false);
  const runCopy = getRunCopy(lang);
  const overviewCopy = getOverviewScreenCopy(lang);

  useEffect(() => {
    if (problemKey && problemKey !== normalizedProblemKey) {
      if (String(problemKey).trim() === PHASE1_PROBLEM_KEY) {
        /* Keep Phase 1 SKU in the URL; JourneyService still uses the normalized storage key. */
      } else {
        const next = new URLSearchParams(params);
        next.set("problemKey", normalizedProblemKey);
        navigate(`/journey/overview?${next.toString()}`, { replace: true });
        return;
      }
    }
    if (problemKey) return;
    navigate(`/journey/select?lang=${encodeURIComponent(lang)}`, { replace: true });
  }, [lang, navigate, normalizedProblemKey, params, problemKey]);

  useEffect(() => {
    if (!normalizedProblemKey || !runId) return;
    JourneyService.setActiveRun(lang, normalizedProblemKey, runId);
  }, [lang, normalizedProblemKey, runId]);

  useEffect(() => {
    if (!normalizedProblemKey) return;
    const { meta: m, intake: i } = JourneyService.loadIntake(lang, normalizedProblemKey);
    const { meta: m2, days } = JourneyService.loadAllDayStates(lang, normalizedProblemKey);
    setMeta(m || m2);
    setIntake(i || null);
    setDayStates(days || {});
    setJourneyRuns(JourneyService.listJourneyRuns(lang, normalizedProblemKey));
    const journal = JourneyService.getFullJournal(lang, normalizedProblemKey);
    setInsights(buildInsights({ lang, entries: journal.entries || [] }));
    if (typeof JourneyService.fetchInsights === "function") {
      JourneyService.fetchInsights(lang, normalizedProblemKey)
        .then((data) => setServerInsights(data?.insights || null))
        .catch(() => {});
    } else {
      setServerInsights(null);
    }
  }, [lang, normalizedProblemKey]);

  useEffect(() => {
    if (!normalizedProblemKey) return;
    let cancelled = false;
    setSignalsTouched(false);
    setMood(3);
    setEnergy(3);

    const applySignals = (signals) => {
      if (!signals || cancelled) return;
      const nextMood = Number(signals.mood || 0);
      const nextEnergy = Number(signals.energy || 0);
      if (nextMood >= 1 && nextMood <= 5) setMood(nextMood);
      if (nextEnergy >= 1 && nextEnergy <= 5) setEnergy(nextEnergy);
    };

    if (typeof JourneyService.getLatestSignals === "function") {
      applySignals(JourneyService.getLatestSignals(lang, normalizedProblemKey));
    }

    if (typeof JourneyService.fetchLatestSignals === "function") {
      JourneyService.fetchLatestSignals(lang, normalizedProblemKey)
        .then(applySignals)
        .catch(() => {});
    }

    return () => {
      cancelled = true;
    };
  }, [lang, normalizedProblemKey]);

  const catalogProblemKey = useMemo(() => {
    if (!normalizedProblemKey) return "";
    return String(problemKey || "").trim() === PHASE1_PROBLEM_KEY ? PHASE1_PROBLEM_KEY : normalizedProblemKey;
  }, [normalizedProblemKey, problemKey]);

  const blueprint = useMemo(
    () => (catalogProblemKey ? getBlueprintForProblem(catalogProblemKey, lang) : null),
    [catalogProblemKey, lang]
  );
  const problemMeta = useMemo(
    () => (catalogProblemKey ? getProblemByKey(catalogProblemKey) : null),
    [catalogProblemKey]
  );
  const problemTitle = useMemo(() => {
    if (!catalogProblemKey) return "";
    return getProblemTitleForLang(catalogProblemKey, lang, { includeDuration: false });
  }, [catalogProblemKey, lang]);
  const totalDays = blueprint?.durationDays || blueprint?.totalDays || problemMeta?.defaultDays || 7;
  const summaryCta = t.summaryCta;
  const dayMetaMap = useMemo(
    () => new Map((blueprint?.days || []).map((item) => [Number(item?.dayNumber), item])),
    [blueprint]
  );

  const intakeItems = useMemo(() => {
    if (!intake) return [];
    return [
      intake.painNow && {
        key: "painNow",
        text: t.intakePainLabel,
        value: intake.painNow,
      },
      intake.desiredEndState && {
        key: "desiredEndState",
        text: t.intakeGoalLabel,
        value: intake.desiredEndState,
      },
      intake.oneWord && {
        key: "oneWord",
        text: t.intakeWordLabel,
        value: intake.oneWord,
      },
    ]
      .filter(Boolean)
      .map((item) => {
        return item;
      });
  }, [intake, lang]);

  const dayList = useMemo(() => {
    const completedDays = Object.entries(dayStates)
      .filter(([, st]) => st && st.completed)
      .map(([d]) => Number(d));
    const allowedDay = Math.min(totalDays, (completedDays.length ? Math.max(...completedDays) : 0) + 1);

    const items = [];
    for (let d = 1; d <= totalDays; d++) {
      const st = dayStates[d];
      let status = "not";
      if (st && st.completed) status = "done";
      else if (st) status = "progress";
      const locked = d > allowedDay;
      items.push({
        day: d,
        state: st,
        status,
        subtitle: resolveUserFacingText(blueprint?.daySubtitles?.[d], {
          lang,
          gender: user?.gender,
          fallback: "",
        }),
        locked,
      });
    }
    return items;
  }, [dayStates, totalDays, blueprint, lang]);

  const lastDay = useMemo(() => {
    const completedDays = Object.entries(dayStates)
      .filter(([, st]) => st && st.completed)
      .map(([d]) => Number(d));
    return Math.min(totalDays, (completedDays.length ? Math.max(...completedDays) : 0) + 1);
  }, [dayStates, totalDays]);

  useEffect(() => {
    if (!signalsTouched || !normalizedProblemKey) return;
    if (typeof JourneyService.saveSignals !== "function") return undefined;

    const timer = window.setTimeout(() => {
      JourneyService.saveSignals({
        lang,
        problemKey: normalizedProblemKey,
        dayNumber: lastDay || 1,
        mood,
        energy,
        totalDays,
      }).catch(() => {});
    }, 260);

    return () => window.clearTimeout(timer);
  }, [signalsTouched, lang, normalizedProblemKey, lastDay, mood, energy, totalDays]);

  const activeRun = useMemo(
    () => journeyRuns.find((run) => run.active) || journeyRuns[0] || null,
    [journeyRuns]
  );
  const archivedRuns = useMemo(
    () => journeyRuns.filter((run) => !run.active),
    [journeyRuns]
  );
  const completedCount = useMemo(
    () => dayList.filter((item) => item.status === "done").length,
    [dayList]
  );
  const journeyComplete = completedCount >= totalDays || Boolean(activeRun?.isComplete);
  const currentDayNumber = useMemo(() => {
    if (journeyComplete) return totalDays;
    return Math.min(activeRun?.nextDay || lastDay || 1, totalDays);
  }, [activeRun, journeyComplete, lastDay, totalDays]);
  const currentDayMeta = dayMetaMap.get(currentDayNumber) || null;
  const currentDayTitle = useMemo(
    () =>
      resolveUserFacingText(currentDayMeta?.title || currentDayMeta?.titleLine, {
        lang,
        gender: user?.gender,
        fallback: "",
      }),
    [currentDayMeta, lang, user?.gender]
  );
  const currentDayPreview = useMemo(() => {
    const openingStep = currentDayMeta?.screens?.find((step) => String(step?.id || "").includes("_opening"));
    const mindfulnessStep = currentDayMeta?.screens?.find((step) => String(step?.id || "").includes("_mindfulness"));
    const lines = [
      ...resolveNodeLines(openingStep?.body, lang, user?.gender),
      ...resolveNodeLines(mindfulnessStep?.body, lang, user?.gender),
    ];
    return pickMeaningfulPreview(lines) || currentDayTitle;
  }, [currentDayMeta, currentDayTitle, lang, user?.gender]);

  const openDay = async (day, locked, status) => {
    if (!normalizedProblemKey) return;
    if (locked) return;
    const next = new URLSearchParams(params);
    next.set("problemKey", catalogProblemKey);
    next.set("day", day);
    if (status === "done") next.set("view", "read");
    else next.delete("view");
    if (day === 1) {
      const key = `disclaimerAccepted:${normalizedProblemKey}`;
      if (localStorage.getItem(key) !== "true") {
        navigate(`/journey/disclaimer?${next.toString()}`);
        return;
      }
    }
    if (day > 1) {
      try {
        const res = await fetch(
          `/api/billing/access?problemKey=${encodeURIComponent(normalizedProblemKey)}&day=${day}`,
          { credentials: "include" }
        );
        const data = await res.json().catch(() => null);
        if (data?.ok && data.access === false) {
          const paywallParams = new URLSearchParams(next);
          paywallParams.set("day", String(day));
          navigate(`/paywall?${paywallParams.toString()}`);
          return;
        }
      } catch {
        const paywallParams = new URLSearchParams(next);
        paywallParams.set("day", String(day));
        navigate(`/paywall?${paywallParams.toString()}`);
        return;
      }
    }
    const dayRoute = DNA_PROBLEM_KEYS.has(normalizedProblemKey) ? "/journey/day-dna" : "/journey/day";
    navigate(`${dayRoute}?${next.toString()}`);
  };

  const goIntake = () => {
    const next = new URLSearchParams(params);
    navigate(`/journey/intake?${next.toString()}`);
  };

  const openJournal = () => {
    const next = new URLSearchParams(params);
    navigate(`/journey/journal?${next.toString()}`);
  };

  const openSummary = () => {
    const next = new URLSearchParams(params);
    next.set("problemKey", catalogProblemKey);
    navigate(`/journey/summary-final?${next.toString()}`);
  };

  const openCurrentFocus = () => {
    if (journeyComplete) {
      openSummary();
      return;
    }
    const currentDayState = dayList.find((item) => item.day === currentDayNumber);
    openDay(currentDayNumber, false, currentDayState?.status || "progress");
  };

  const timelineItems = useMemo(() => {
    return dayList.map(({ day, status, subtitle, locked }) => {
      const meta = dayMetaMap.get(day);
      const title =
        resolveUserFacingText(meta?.title || meta?.titleLine, {
          lang,
          gender: user?.gender,
          fallback: subtitle || `${t.dayLabel} ${day}`,
        }) || `${t.dayLabel} ${day}`;
      const openingStep = meta?.screens?.find((step) => String(step?.id || "").includes("_opening"));
      const preview =
        pickMeaningfulPreview(resolveNodeLines(openingStep?.body, lang, user?.gender)) ||
        subtitle ||
        "";

      let visualState = "future";
      if (status === "done") visualState = "completed";
      if (!journeyComplete && day === currentDayNumber) visualState = "current";

      const statusText =
        visualState === "current"
          ? overviewCopy.currentLine
          : visualState === "completed"
            ? t.dayStatusCompleted
            : overviewCopy.futureDay;

      return {
        day,
        title,
        preview,
        locked,
        status,
        visualState,
        statusText,
      };
    });
  }, [currentDayNumber, dayList, dayMetaMap, journeyComplete, lang, overviewCopy, t.dayLabel, t.dayStatusCompleted, user?.gender]);

  const switchRun = (runId) => {
    if (!normalizedProblemKey) return;
    JourneyService.setActiveRun(lang, normalizedProblemKey, runId);
    const { intake: nextIntake } = JourneyService.loadIntake(lang, normalizedProblemKey, { runId });
    const { days } = JourneyService.loadAllDayStates(lang, normalizedProblemKey, undefined, { runId });
    const journal = JourneyService.getFullJournal(lang, normalizedProblemKey, { runId });
    setIntake(nextIntake || null);
    setDayStates(days || {});
    setJourneyRuns(JourneyService.listJourneyRuns(lang, normalizedProblemKey));
    setInsights(buildInsights({ lang, entries: journal.entries || [] }));
  };

  const startNewRun = () => {
    if (!normalizedProblemKey || runBusy) return;
    if (!intake) {
      const next = new URLSearchParams(params);
      next.set("problemKey", catalogProblemKey);
      navigate(`/journey/intake?${next.toString()}`);
      return;
    }
    setRunBusy(true);
    try {
      JourneyService.startNewJourney({
        lang,
        problemKey: normalizedProblemKey,
        intake,
      });
      navigate(`/journey/language-mirror?problemKey=${encodeURIComponent(catalogProblemKey)}&lang=${encodeURIComponent(lang)}`);
    } finally {
      setRunBusy(false);
    }
  };

  const moodExperience = useMemo(
    () => getMoodExperienceCopy(lang, user?.gender, problemTitle),
    [lang, user?.gender, problemTitle]
  );
  const moodFeedback = moodExperience.states[mood] || moodExperience.states[3];
  const moodProgress = ((mood - 1) / 4) * 100;

  if (!normalizedProblemKey) return null;

  return (
    <div className="journey-overview" dir={dir}>
      <header className="jo-header">
        <div className="jo-header-copy">
          <div className="jo-header-kicker">{t.pageTitle}</div>
          <h1 className="jo-journey-title">{problemTitle}</h1>
          <p className="jo-problem-line">{t.presenceLine}</p>
          <p className="jo-presence">
            <span className="jo-presence-dot" aria-hidden="true" />
            <span className="jo-presence-text">
              {journeyComplete
                ? runCopy.completedJourney
                : runCopy.dayProgress(currentDayNumber, totalDays)}
            </span>
          </p>
        </div>
        <div className="jo-header-actions">
          <button className="jo-secondary" onClick={() => navigate(`/journey/select?${params.toString()}&fresh=1`)}>
            {t.chooseProblem}
          </button>
        </div>
      </header>

      <div className="jo-primary-cta-row">
        <button type="button" className="jo-primary jo-primary-hero jo-primary-cta-priority" onClick={openCurrentFocus}>
          {journeyComplete ? summaryCta : overviewCopy.heroCta}
        </button>
      </div>

      <section className="jo-hero jo-hero--current-day">
        <div className="jo-hero-path-wrap" aria-hidden="true">
          <div className="jo-path-scroll jo-path-scroll--hero">
            <div className="jo-path-rail">
              {timelineItems.map((item) => (
                <span key={`path-hero-${item.day}`} className={`jo-path-node state-${item.visualState}`} />
              ))}
            </div>
          </div>
        </div>
        <div className="jo-hero-inner">
          <div className="jo-hero-copy">
            <div className="jo-hero-topline">
              <span className="jo-hero-badge">
                {journeyComplete ? runCopy.completed : overviewCopy.currentBadge}
              </span>
              <span className="jo-hero-progress">
                {journeyComplete ? `${completedCount}/${totalDays}` : runCopy.dayProgress(currentDayNumber, totalDays)}
              </span>
            </div>
            <div className="jo-hero-kicker">{overviewCopy.heroKicker}</div>
            <h2 className="jo-hero-title">
              {journeyComplete ? runCopy.completedJourney : runCopy.waitingDay(currentDayNumber)}
            </h2>
            {!!currentDayTitle && <p className="jo-hero-emotion">{currentDayTitle}</p>}
            {!!currentDayPreview && currentDayPreview !== currentDayTitle && (
              <p className="jo-hero-preview">{currentDayPreview}</p>
            )}
            <div className="jo-hero-meta-row">
              <span className="jo-hero-meta-pill">
                {t.dayLabel} {currentDayNumber}/{totalDays}
              </span>
              {activeRun?.startedAt && (
                <span className="jo-hero-meta-pill">
                  {runCopy.startedOn} {formatRunDate(activeRun.startedAt, lang)}
                </span>
              )}
            </div>
            {!!activeRun?.anchorText && <div className="jo-run-anchor">"{activeRun.anchorText}"</div>}
          </div>
          <div className="jo-hero-secondary-actions jo-hero-secondary-actions--inline">
            <button type="button" className="jo-secondary" onClick={openJournal}>
              {t.journalCta}
            </button>
            <button type="button" className="jo-link" onClick={startNewRun} disabled={runBusy}>
              {runCopy.startNew}
            </button>
          </div>
        </div>
      </section>

      <section className="jo-card jo-timeline-card">
        <div className="jo-section-head">
          <div>
            <div className="jo-section-kicker">{overviewCopy.timelineTitle}</div>
            <h3>{t.daysSummaryTitle}</h3>
            <p className="jo-hint jo-section-hint">{overviewCopy.timelineSubtitle}</p>
          </div>
        </div>
        <div className="jo-days-grid">
          {timelineItems.map(({ day, title, preview, status, locked, visualState, statusText }) => {
            return (
              <article key={day} className={`jo-day-card state-${visualState} ${locked ? "is-locked" : ""}`}>
                <div className="jo-day-top">
                  <div className="jo-day-rail" aria-hidden="true">
                    <span className={`jo-day-dot state-${visualState}`} />
                  </div>
                  <div className="jo-day-head">
                    <div className="jo-day-number">
                      {t.dayLabel} {day}
                    </div>
                    <div className={`jo-day-badge state-${visualState}`}>
                      {visualState === "current"
                        ? overviewCopy.currentBadge
                        : visualState === "completed"
                          ? t.dayStatusCompleted
                          : overviewCopy.futureDay}
                    </div>
                  </div>
                </div>
                <div className="jo-day-title">{title}</div>
                {visualState === "current" && !!preview && (
                  <div className="jo-day-sub">{preview}</div>
                )}
                {visualState !== "current" && (
                  <div className="jo-day-status">{statusText}</div>
                )}
                {visualState === "current" && (
                  <div className="jo-day-now-line">{statusText}</div>
                )}
                {visualState === "completed" && (
                  <button type="button" className="jo-day-link" onClick={() => openDay(day, locked, status)}>
                    {overviewCopy.reopenDay}
                  </button>
                )}
                {visualState === "future" && (
                  <div className="jo-day-status jo-day-status-muted">{t.dayLocked}</div>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <div className="jo-snapshot-grid">
        <section className="jo-card jo-snapshot-card">
          <h3>{t.intakeSummaryTitle}</h3>
          {intakeItems.length === 0 && (
            <p className="jo-hint">
              {t.noIntakeYet}{" "}
              <button className="jo-link" onClick={goIntake}>
                {t.goToIntake}
              </button>
            </p>
          )}
          {intakeItems.map((item) => (
            <div key={item.key} className="jo-intake-row">
              <div className="jo-intake-label">{item.text}</div>
              <div className="jo-intake-value">{item.value}</div>
            </div>
          ))}
        </section>

        <section className="jo-card jo-snapshot-card jo-snapshot-card-signal">
          <h3>{moodExperience.title}</h3>
          <div className="jo-signals">
            <p className="jo-signal-subtitle">{moodExperience.subtitle}</p>
            <div className="jo-mood-shell">
              <div
                className="jo-mood-slider-wrap"
                style={{ "--jo-mood-progress": `${moodProgress}%` }}
              >
                <input
                  type="range"
                  min="1"
                  max="5"
                  step="1"
                  value={mood}
                  className="jo-mood-range"
                  aria-label={moodExperience.title}
                  aria-valuemin={1}
                  aria-valuemax={5}
                  aria-valuenow={mood}
                  aria-valuetext={moodFeedback.lines.join(" ")}
                  onChange={(e) => {
                    setMood(Number(e.target.value));
                    setSignalsTouched(true);
                  }}
                />
                <div className="jo-mood-ticks" aria-hidden="true">
                  {[1, 2, 3, 4, 5].map((val) => (
                    <span key={`mood-tick-${val}`}>{val}</span>
                  ))}
                </div>
              </div>

              <div className="jo-mood-feedback-shell">
                <div key={`mood-feedback-${mood}`} className="jo-mood-feedback">
                  <div className="jo-mood-icon" aria-hidden="true">
                    {moodFeedback.icon}
                  </div>
                  <div className="jo-mood-feedback-copy">
                    {moodFeedback.lines.map((line, index) => (
                      <div key={`mood-line-${mood}-${index}`} className="jo-mood-feedback-line">
                        {line}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="jo-energy-strip">
              <div className="jo-energy-head">
                <span className="jo-energy-label">{t.energyTitle}</span>
                <span className="jo-energy-value">{energy}</span>
              </div>
              <div className="jo-energy-pills" role="group" aria-label={t.energyTitle}>
                {[1, 2, 3, 4, 5].map((val) => (
                  <button
                    key={`energy-${val}`}
                    type="button"
                    className={`jo-energy-pill ${energy === val ? "is-active" : ""}`}
                    aria-pressed={energy === val}
                    onClick={() => {
                      setEnergy(val);
                      setSignalsTouched(true);
                    }}
                  >
                    {val}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {insights && (
          <section className="jo-card jo-snapshot-card">
            <h3>{t.insightsTitle}</h3>
            <div className="jo-insights">
              <div className="jo-insight">
                <div className="jo-insight-label">{t.insightsRepeating}</div>
                <div className="jo-insight-value">
                  {(serverInsights?.repeatingWords || insights.repeatingWords).length
                    ? (serverInsights?.repeatingWords || insights.repeatingWords).join(", ")
                    : "—"}
                </div>
              </div>
              <div className="jo-insight">
                <div className="jo-insight-label">{t.insightsHelped}</div>
                {(serverInsights?.whatHelped || insights.whatHelped).map((line, idx) => (
                  <div key={`helped-${idx}`} className="jo-insight-value">
                    {line}
                  </div>
                ))}
              </div>
              <div className="jo-insight">
                <div className="jo-insight-label">{t.insightsNext}</div>
                <div className="jo-insight-value">{serverInsights?.nextStep || insights.nextStep}</div>
              </div>
            </div>
          </section>
        )}

        {(archivedRuns.length > 0 || activeRun) && (
          <section className="jo-card jo-snapshot-card">
            <div className="jo-run-head">
              <h3>{runCopy.historySection}</h3>
            </div>
            {archivedRuns.length === 0 && <p className="jo-hint">{runCopy.noRuns}</p>}
            {archivedRuns.length > 0 && <p className="jo-hint">{runCopy.historyHint}</p>}
            <div className="jo-runs-list">
              {archivedRuns.map((run) => (
                <div key={run.id} className={`jo-run-card ${run.active ? "active" : ""}`}>
                  <div className="jo-run-top">
                    <div className="jo-run-title">{runCopy.runLabel(run.index)}</div>
                    <div className="jo-run-chip">{run.isComplete ? runCopy.completed : runCopy.inProgress}</div>
                  </div>
                  <div className="jo-run-meta">
                    {runCopy.startedOn} {formatRunDate(run.startedAt, lang)}
                  </div>
                  <div className="jo-run-meta">
                    {run.isComplete ? runCopy.completed : runCopy.dayProgress(run.nextDay, run.totalDays)}
                  </div>
                  {!!run.anchorText && <div className="jo-run-anchor">"{run.anchorText}"</div>}
                  <button
                    type="button"
                    className="jo-secondary"
                    onClick={() => {
                      switchRun(run.id);
                    }}
                  >
                    {runCopy.switchTo}
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

